/**
 * Subscription plan API client.
 *
 * Super Admin endpoints (require superuser):
 *   GET    /api/superadmin/subscription-plans
 *   POST   /api/superadmin/subscription-plans
 *   PATCH  /api/superadmin/subscription-plans/:id
 *   DELETE /api/superadmin/subscription-plans/:id   (permanent)
 *
 *   GET    /api/superadmin/organizations/:org_id/billing-overview
 *
 * Org-admin / authenticated:
 *   GET    /api/v2/billing/plans
 */

import apiClient from './config';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type PlanType = 'free' | 'paid';
export type BillingInterval = 'free' | 'day' | 'month' | 'year';
export type FeatureFlagKey =
  | 'testcase_generation'
  | 'test_execution'
  | 'synthetic_data'
  | 'byo_key'
  | 'data_validation_agent';

export interface SubscriptionPlanFull
{
    id: number;
    name: string;
    description: string | null;
    plan_type: PlanType;
    price_cents: number;
    price_usd: number;
    currency: string;
    billing_interval: BillingInterval;
    user_limit: number | null;
    feature_flags: FeatureFlagKey[];
    free_token_quota: number | null;
    free_quota_period_days: number | null;
    is_active: boolean;
    org_id: number | null;
    is_custom: boolean;
}

export interface SubscriptionPlanCreateInput
{
    name: string;
    description?: string | null;
    plan_type?: PlanType;
    price_cents?: number;
    currency?: string;
    billing_interval?: BillingInterval;
    user_limit?: number | null;
    feature_flags?: FeatureFlagKey[];
    free_token_quota?: number | null;
    free_quota_period_days?: number | null;
    is_active?: boolean;
    org_id?: number | null;
}

export interface SubscriptionPlanUpdateInput
{
    name?: string;
    description?: string | null;
    plan_type?: PlanType;
    price_cents?: number;
    currency?: string;
    billing_interval?: BillingInterval;
    user_limit?: number | null;
    user_limit_set?: boolean;
    feature_flags?: FeatureFlagKey[];
    free_token_quota?: number | null;
    free_token_quota_set?: boolean;
    free_quota_period_days?: number | null;
    free_quota_period_days_set?: boolean;
    is_active?: boolean;
    org_id?: number | null;
    org_id_set?: boolean;
}

export interface AvailablePlansResponse
{
    paid_plans: SubscriptionPlanFull[];
    free_plan: SubscriptionPlanFull | null;
    llm_usage_recharge_amount_cents: number;
    llm_usage_recharge_amount_usd: number;
    llm_usage_currency: string;
}

export interface OrgBillingOverview
{
    organization_id: number;
    organization_name: string | null;
    quota: {
        has_active_subscription: boolean;
        subscription_status: string | null;
        free_tokens_allocated: number;
        free_tokens_consumed: number;
        free_tokens_remaining: number;
        free_overage_debt: number;
        free_overage_debt_cents: number;
        free_overage_debt_usd: number;
        platform_metered_tokens: number;
        platform_metered_charge_usd: number;
        platform_usage_credit_tokens: number;
        platform_usage_credit_charge_usd: number;
        all_time_paid_platform_tokens: number;
        byo_recorded_tokens: number;
        byo_recorded_charge_usd: number;
        active_balance_source: string | null;
        may_run_job: boolean;
        block_reason: string | null;
        next_action_required: string | null;
        period_start: string | null;
        period_end: string | null;
    };
    payments: Array<{
        id: number;
        amount_cents: number;
        amount_usd: number;
        currency: string;
        status: string;
        stripe_payment_intent_id: string | null;
        stripe_invoice_id: string | null;
        stripe_charge_id: string | null;
        receipt_url: string | null;
        payment_type: string;
        recharge_type: string | null;
        plan_name_snapshot?: string | null;
        payment_context?: string | null;
        period_start: string | null;
        period_end: string | null;
        created_at: string;
        attempt_id?: number | null;
        row_kind?: 'payment' | 'attempt' | 'refund' | 'related';
        parent_payment_id?: number | null;
        related_payment_created_at?: string | null;
        related_payment_paid_at?: string | null;
        related_payment_amount_cents?: number | null;
        related_payment_receipt_url?: string | null;
        related_payment_period_start?: string | null;
        related_payment_period_end?: string | null;
        refund_events?: Array<{
            id: number;
            amount_cents: number;
            created_at: string;
        }>;
    }>;
    token_usage: Array<{
        id: number;
        job_id: number | null;
        user_id: number | null;
        chat_session_id?: string | null;
        usage_group_key?: string | null;
        usage_label?: string | null;
        model: string | null;
        workflow_profile: string | null;
        stage: string | null;
        provider: string | null;
        key_source: string;
        billable: boolean;
        event_count?: number;
        prompt_tokens: number;
        completion_tokens: number;
        cached_input_tokens: number;
        image_tokens: number;
        total_tokens: number;
        billable_tokens: number;
        non_billable_tokens: number;
        provider_cost_usd: number;
        customer_charge_usd: number;
        created_at: string;
    }>;
    ledger: Array<{
        id: number;
        event_type: string;
        token_delta: number;
        balance_bucket: string;
        subscription_id: number | null;
        job_id: number | null;
        notes: string | null;
        created_at: string;
    }>;
    llm_usage_credits: {
        organization_id: number;
        balance_cents: number;
        balance_usd: number;
        pending_overage_debt_cents: number;
        pending_overage_debt_usd: number;
        free_overage_debt_cents: number;
        free_overage_debt_usd: number;
        available_balance_after_debt_cents: number;
        available_balance_after_debt_usd: number;
        currency: string;
        status: string;
        recharge_amount_cents: number;
        recharge_amount_usd: number;
        auto_recharge_threshold_cents: number;
        auto_recharge_threshold_usd: number;
        last_recharged_at: string | null;
        recharge_required: boolean;
        failure_message: string | null;
    };
    llm_usage_credit_ledger: Array<{
        id: number;
        entry_type: string;
        amount_cents: number;
        amount_usd: number;
        balance_before_cents: number;
        balance_after_cents: number;
        pending_overage_before_cents: number;
        pending_overage_after_cents: number;
        currency: string;
        usage_event_id: number | null;
        payment_id: number | null;
        stripe_payment_intent_id: string | null;
        stripe_charge_id: string | null;
        stripe_receipt_url: string | null;
        stripe_error: string | null;
        created_at: string;
    }>;
    current_plan: SubscriptionPlanFull | null;
}

export interface SuperAdminBillingOverviewFilterParams
{
    paymentLimit?: number;
    usageLimit?: number;
    payment_search?: string;
    payment_status?: string;
    payment_type?: string;
    payment_date_from?: string;
    payment_date_to?: string;
    token_usage_search?: string;
    token_usage_workflow_profile?: string;
    token_usage_key_source?: string;
    token_usage_billable?: boolean;
    token_usage_date_from?: string;
    token_usage_date_to?: string;
    quota_ledger_search?: string;
    quota_ledger_balance_bucket?: string;
    quota_ledger_event_type?: string;
    quota_ledger_date_from?: string;
    quota_ledger_date_to?: string;
    credit_ledger_search?: string;
    credit_ledger_entry_type?: string;
    credit_ledger_date_from?: string;
    credit_ledger_date_to?: string;
}

const SA_BASE = '/api/superadmin';

// ---------------------------------------------------------------------------
// Subscription Plans (super admin)
// ---------------------------------------------------------------------------

export async function listSubscriptionPlans(opts?: { planType?: PlanType }): Promise<SubscriptionPlanFull[]>
{
    const { data } = await apiClient.get<SubscriptionPlanFull[]>(`${SA_BASE}/subscription-plans`, {
        params: {
            plan_type: opts?.planType,
        },
    });
    return data;
}

export async function createSubscriptionPlan(input: SubscriptionPlanCreateInput): Promise<SubscriptionPlanFull>
{
    const { data } = await apiClient.post<SubscriptionPlanFull>(`${SA_BASE}/subscription-plans`, input);
    return data;
}

export async function updateSubscriptionPlan(
    id: number,
    input: SubscriptionPlanUpdateInput,
): Promise<SubscriptionPlanFull>
{
    const { data } = await apiClient.patch<SubscriptionPlanFull>(`${SA_BASE}/subscription-plans/${id}`, input);
    return data;
}

export async function deleteSubscriptionPlan(id: number): Promise<SubscriptionPlanFull>
{
    const { data } = await apiClient.delete<SubscriptionPlanFull>(`${SA_BASE}/subscription-plans/${id}`);
    return data;
}

// ---------------------------------------------------------------------------
// Org-admin facing
// ---------------------------------------------------------------------------

export async function fetchAvailablePlans(): Promise<AvailablePlansResponse>
{
    const { data } = await apiClient.get<AvailablePlansResponse>('/api/v2/billing/plans');
    return data;
}

export interface CurrentPlanResponse
{
    plan: SubscriptionPlanFull | null;
    is_free_plan: boolean;
    effective_free_token_quota: number | null;
    effective_user_limit: number | null;
    free_quota_source: string | null;
    is_beta_quota: boolean;
    free_quota_started_at: string | null;
    free_quota_expires_at: string | null;
    free_quota_period_days: number | null;
}

export async function fetchCurrentPlan(): Promise<CurrentPlanResponse>
{
    const { data } = await apiClient.get<CurrentPlanResponse>('/api/v2/billing/current-plan');
    return data;
}

// ---------------------------------------------------------------------------
// Super-admin organization billing viewer
// ---------------------------------------------------------------------------

export async function fetchOrganizationBillingOverview(
    orgId: number,
    opts?: SuperAdminBillingOverviewFilterParams,
): Promise<OrgBillingOverview>
{
    const { data } = await apiClient.get<OrgBillingOverview>(
        `${SA_BASE}/organizations/${orgId}/billing-overview`,
        {
            params: {
                payment_limit: opts?.paymentLimit ?? 50,
                usage_limit: opts?.usageLimit ?? 50,
                payment_search: opts?.payment_search,
                payment_status: opts?.payment_status,
                payment_type: opts?.payment_type,
                payment_date_from: opts?.payment_date_from,
                payment_date_to: opts?.payment_date_to,
                token_usage_search: opts?.token_usage_search,
                token_usage_workflow_profile: opts?.token_usage_workflow_profile,
                token_usage_key_source: opts?.token_usage_key_source,
                token_usage_billable: opts?.token_usage_billable,
                token_usage_date_from: opts?.token_usage_date_from,
                token_usage_date_to: opts?.token_usage_date_to,
                quota_ledger_search: opts?.quota_ledger_search,
                quota_ledger_balance_bucket: opts?.quota_ledger_balance_bucket,
                quota_ledger_event_type: opts?.quota_ledger_event_type,
                quota_ledger_date_from: opts?.quota_ledger_date_from,
                quota_ledger_date_to: opts?.quota_ledger_date_to,
                credit_ledger_search: opts?.credit_ledger_search,
                credit_ledger_entry_type: opts?.credit_ledger_entry_type,
                credit_ledger_date_from: opts?.credit_ledger_date_from,
                credit_ledger_date_to: opts?.credit_ledger_date_to,
            },
        },
    );
    return data;
}

// ---------------------------------------------------------------------------
// Super-admin: organization custom plans
// ---------------------------------------------------------------------------

export interface OrgCustomPlanCreateInput
{
    name: string;
    description?: string | null;
    price_cents?: number;
    billing_interval?: BillingInterval;
    user_limit?: number | null;
    feature_flags?: FeatureFlagKey[];
    is_active?: boolean;
}

export async function listOrgCustomPlans(orgId: number): Promise<SubscriptionPlanFull[]>
{
    const { data } = await apiClient.get<SubscriptionPlanFull[]>(
        `${SA_BASE}/organizations/${orgId}/custom-plans`,
    );
    return data;
}

export async function createOrgCustomPlan(
    orgId: number,
    input: OrgCustomPlanCreateInput,
): Promise<SubscriptionPlanFull>
{
    const { data } = await apiClient.post<SubscriptionPlanFull>(
        `${SA_BASE}/organizations/${orgId}/custom-plans`,
        input,
    );
    return data;
}

export async function deleteOrgCustomPlan(
    orgId: number,
    planId: number,
): Promise<SubscriptionPlanFull>
{
    const { data } = await apiClient.delete<SubscriptionPlanFull>(
        `${SA_BASE}/organizations/${orgId}/custom-plans/${planId}`,
    );
    return data;
}
